<template>
  <div>
    <div class="row bg-white m-2 p-3 rounded">
      <!-- ===================== SELECCIONADOS ===================== -->
      <div v-if="!isReadOnly" class="my-3 p-2 col-12">
        <custom-separator class="my-4" text="Contratos Seleccionados" />

        <div
          v-if="model.length > 0"
          class="d-flex justify-content-end align-items-center mt-4 pr-2"
        >
          <div class="d-flex align-items-center mr-3">
            <check-box
              :class="[{ 'text-green': model.length > 0 }]"
              :text="`Deseleccionar Todo (${model.length})`"
              :value="true"
              @input="onDeselectAllSelected"
            />
          </div>
        </div>
      </div>

      <div class="col-12 my-2">
        <slot name="contract-error" />
      </div>

      <div class="col-12">
        <div v-for="row in model" :key="'sel-' + row.contractNumber">
          <contract-row
            :data="{ ...row, checked: true }"
            :is-read-only="isReadOnly"
            @toggle="onToggleFromSelected"
          />
        </div>

        <custom-result-not-found
          v-if="model.length <= 0"
          text="No hay información disponible."
        />
      </div>

      <!-- ===================== BUSCADOR ===================== -->
      <div v-if="!isReadOnly && isCompetitionProcessFromMipe" class="col my-3">
        <custom-input
          v-model="contractName"
          icon="fal fa-search color-primary-2"
          left-icon
          placeholder="Número de Contrato o Proveedor"
          prepend-icon-type="success"
        />
      </div>
      <div
        v-if="!isReadOnly && isCompetitionProcessFromMipe"
        class="col-auto my-3"
      >
        <action-button
          i="fal fa-search"
          text="Buscar"
          variant="success px-3"
          @click="searchByCriteria"
        />
      </div>

      <!-- Formulario de añadir nuevo contrato -->
      <div v-if="isAddMode" class="col-12">
        <add-contract
          :contractors="contractors"
          @add="onAddNewContract"
          @cancel="onToggleAddForm"
        />
      </div>

      <!-- ===================== RESULTADOS ===================== -->
      <div v-if="!isReadOnly && !isAddMode" class="col-12">
        <custom-separator class="my-4" text="Resultados" />

        <div
          v-if="filteredResults.length > 0"
          class="d-flex justify-content-end align-items-center my-3 p-2"
        >
          <div class="d-flex align-items-center mr-3">
            <check-box
              :class="[{ 'text-green': filteredResults.length > 0 }]"
              :text="`Seleccionar Todo (${filteredResults.length})`"
              :value="false"
              @input="onSelectAllResults"
            />
          </div>

          <a
            class="text-green pointer"
            href="#"
            @click.prevent="onToggleAddForm"
          >
            <i class="fas fa-file-plus mr-2" />
            Añadir Contrato
          </a>
        </div>

        <div v-for="row in filteredResults" :key="'res-' + row.id">
          <contract-row
            :data="{ ...row, checked: false }"
            :is-read-only="isReadOnly"
            @toggle="onToggleFromResults"
          />
        </div>

        <div
          v-if="filteredResults.length === 0"
          class="d-flex justify content-center flex-column align-items-center my-5"
        >
          <custom-result-not-found text="No se encontró Contrato." />

          <a
            class="text-green pointer"
            href="#"
            @click.prevent="onToggleAddForm"
          >
            <i class="fas fa-file-plus mr-2" />
            Añadir Contrato
          </a>
        </div>

        <pagination
          v-if="showPagination"
          :data="pagination"
          show-text
          @pagination-go-page="goToPageHandler"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Components
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import CheckBox from 'CheckBox';
import ContractRow from '../ContractRow.vue';
import AddContract from '../AddContract.vue';

// Constants
const DEFAULT_PAGE_SIZE = 5;

export default {
  name: 'Contracts',
  components: {
    ActionButton,
    CustomInput,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    CheckBox,
    ContractRow,
    AddContract,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    contracts: {
      type: Object,
      default: () => ({
        data: [],
        totalItemCount: 0,
        currentPage: 1,
        count: 0,
        pageCount: 0,
      }),
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    isCompetitionProcessFromMipe: {
      type: Boolean,
      default: () => false,
    },
    contractors: { type: Array, default: () => [] },
  },
  data() {
    return {
      contractName: '',
      isAddMode: false,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    pagination() {
      const c = this.contracts || {};
      return {
        currentPage: c.currentPage ?? 1,
        totalItemCount: c.totalItemCount ?? 0,
        pageCount: c.pageCount ?? 0,
        count: c.count ?? 0,
        pageSize: DEFAULT_PAGE_SIZE,
      };
    },
    results() {
      return (this.contracts?.data ?? []).map((x) => ({
        ...x,
        quantity: x.amendments?.length ?? 0,
        amount: x.balanceMaxAmt,
        checked: x.checked ?? false,
      }));
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    selectedIds() {
      return new Set(this.model.map((x) => x.id));
    },
    filteredResults() {
      return this.results.filter((r) => !this.selectedIds.has(r.id));
    },
  },
  methods: {
    onToggleAddForm() {
      this.isAddMode = !this.isAddMode;
    },
    onAddNewContract(newContract) {
      this.model = [...this.model, { ...newContract, checked: true }];
      this.isAddMode = false;
    },
    goToPageHandler(page) {
      this.$emit('load-contracts', {
        name: this.contractName,
        currentPage: page,
        pageSize: DEFAULT_PAGE_SIZE,
      });
    },
    searchByCriteria() {
      this.$emit('load-contracts', {
        name: this.contractName,
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      });
    },
    onToggleFromResults({ id, checked }) {
      if (!checked) return;
      const item = this.results.find((r) => r.id === id);
      if (item && !this.selectedIds.has(id)) {
        this.model = [...this.model, { ...item, checked: true }];
      }
    },
    onToggleFromSelected({ checked, contractNumber }) {
      if (checked) return;

      this.model = this.model.filter(
        (s) => s.contractNumber !== contractNumber
      );
    },
    onSelectAllResults() {
      const toAdd = this.filteredResults.map((x) => ({ ...x, checked: true }));
      if (toAdd.length) this.model = [...this.model, ...toAdd];
    },
    onDeselectAllSelected() {
      this.model = [];
    },
  },
};
</script>

<style scoped lang="scss"></style>
